import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface CartItem {
  id: string; // unique line id (productId + skuId)
  productId: number;
  productName: string;
  image: string;
  shopName: string;
  skuId: number | null;
  skuLabel: string | null;
  options: string[]; // ["Cream", "Medium"]
  unitPrice: number;
  originalPrice: number;
  currency: string;
  quantity: number;
  maxStock: number; // available stock for this sku/product
}

interface ShopContextValue {
  // Favorites
  favorites: number[];
  isFavorite: (productId: number) => boolean;
  toggleFavorite: (productId: number) => void;

  // Cart
  cart: CartItem[];
  cartCount: number;
  cartSubtotal: number;
  addToCart: (item: Omit<CartItem, "id">) => void;
  updateQuantity: (lineId: string, quantity: number) => void;
  removeFromCart: (lineId: string) => void;
  clearCart: () => void;

  // Drawer
  cartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;

  // Toast
  toast: { id: number; message: string } | null;
  showToast: (message: string) => void;
}

const ShopContext = createContext<ShopContextValue | null>(null);

const FAV_KEY = "littleloom.favorites";
const CART_KEY = "littleloom.cart";

function loadFavorites(): number[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "number") : [];
  } catch {
    return [];
  }
}

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function buildLineId(productId: number, skuId: number | null): string {
  return `${productId}:${skuId ?? "default"}`;
}

export function ShopProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<number[]>(() => loadFavorites());
  const [cart, setCart] = useState<CartItem[]>(() => loadCart());
  const [cartOpen, setCartOpen] = useState(false);
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null);

  // Persist favorites
  useEffect(() => {
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(favorites));
    } catch {
      // ignore
    }
  }, [favorites]);

  // Persist cart
  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {
      // ignore
    }
  }, [cart]);

  const isFavorite = useCallback(
    (productId: number) => favorites.includes(productId),
    [favorites],
  );

  const toggleFavorite = useCallback((productId: number) => {
    setFavorites((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      }
      return [...prev, productId];
    });
  }, []);

  const showToast = useCallback((message: string) => {
    const id = Date.now();
    setToast({ id, message });
    window.setTimeout(() => {
      setToast((cur) => (cur && cur.id === id ? null : cur));
    }, 2400);
  }, []);

  const addToCart = useCallback((item: Omit<CartItem, "id">) => {
    const lineId = buildLineId(item.productId, item.skuId);
    let clampMessage: string | null = null;
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.id === lineId);
      if (idx >= 0) {
        const next = [...prev];
        const cur = next[idx];
        const requested = cur.quantity + item.quantity;
        const cap = item.maxStock;
        const nextQty = Math.min(cap, requested);
        if (requested > cap) {
          clampMessage =
            cap <= 0
              ? "Esgotado — não foi possível adicionar mais"
              : `Só restam ${cap} em estoque — mantivemos o máximo no carrinho`;
        }
        next[idx] = { ...cur, quantity: nextQty, maxStock: item.maxStock };
        return next;
      }
      // New line — also clamp the initial add
      const cap = item.maxStock;
      const initialQty = Math.min(cap, Math.max(1, item.quantity));
      if (item.quantity > cap) {
        clampMessage =
          cap <= 0
            ? "Esgotado — não foi possível adicionar ao carrinho"
            : `Só restam ${cap} em estoque — adicionamos o máximo possível`;
      }
      return [...prev, { ...item, quantity: initialQty, id: lineId }];
    });
    if (clampMessage) {
      const msg = clampMessage;
      // defer so it shows after the cart drawer opens
      window.setTimeout(() => showToast(msg), 60);
    }
  }, [showToast]);

  const updateQuantity = useCallback((lineId: string, quantity: number) => {
    setCart((prev) =>
      prev
        .map((l) =>
          l.id === lineId
            ? { ...l, quantity: Math.max(1, Math.min(l.maxStock, quantity)) }
            : l,
        )
        .filter((l) => l.quantity > 0),
    );
  }, []);

  const removeFromCart = useCallback((lineId: string) => {
    setCart((prev) => prev.filter((l) => l.id !== lineId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);

  const cartCount = useMemo(
    () => cart.reduce((sum, l) => sum + l.quantity, 0),
    [cart],
  );

  const cartSubtotal = useMemo(
    () => cart.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0),
    [cart],
  );

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (cartOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [cartOpen]);

  const value: ShopContextValue = {
    favorites,
    isFavorite,
    toggleFavorite,
    cart,
    cartCount,
    cartSubtotal,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    cartOpen,
    openCart,
    closeCart,
    toast,
    showToast,
  };

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop(): ShopContextValue {
  const ctx = useContext(ShopContext);
  if (!ctx) {
    throw new Error("useShop must be used within ShopProvider");
  }
  return ctx;
}